import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { workbenchApi } from '../../api/workbench'
import { RefusedError } from '../../api/client'
import { formatRelative } from '../../lib/format'
import { modelWords, PART_WORDS } from '../../lib/workbench'
import type { ChallengeView, ChallengeWord } from '../../types/workbench'

// THE CHALLENGER (REQ-108) — a tab pulled out from the right of the draft. Don, 2026-09-25: "if I say, no, I think we
// should do it this way, I don't want it to just be like, okay … If it doesn't, why? Tell me what I should be using
// and what's the logic behind it" — and the note he regretted: "why is this guy going to open this email? And if he
// does open it, why is he going to respond to me?" A second, different model reads the draft as its recipient would
// and answers those two questions first, then the holes, each with its logic and an instruction. "Apply" puts the
// instruction into the Wordsmith for the rep to send; the conversation revises and records a version. Nothing is
// changed from here.

const why = (e: unknown) => (e instanceof RefusedError ? e.refusal.message : e instanceof Error ? e.message : String(e))

const WORD: Record<ChallengeWord, string> = { yes: 'Yes', maybe: 'Maybe', no: 'No' }

function Verdict({ question, word, reason }: { question: string; word: ChallengeWord; reason: string }) {
  return (
    <div className={`challenge-verdict ${word}`}>
      <span className="challenge-question">{question}</span>
      <strong className="challenge-word">{WORD[word]}</strong>
      {reason && <p>{reason}</p>}
    </div>
  )
}

export function Challenger({ spoke, companyId, personId, opportunityId = null, onApply, busy }: {
  spoke: string; companyId: string; personId: string
  /** the opportunity the draft serves (REQ-121): the review is of that draft, so it is keyed by it */
  opportunityId?: string | null
  /** an instruction handed to the conversation (the rep edits and sends it there) */
  onApply: (instruction: string) => void
  busy?: boolean
}) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const key = ['workbench-challenge', spoke, companyId, personId, opportunityId]
  const latest = useQuery({ queryKey: key, queryFn: () => workbenchApi.latestChallenge(spoke, companyId, personId, opportunityId ?? undefined), retry: false, staleTime: 30_000 })
  const run = useMutation({
    mutationFn: () => workbenchApi.challenge(spoke, companyId, personId, opportunityId ?? undefined),
    onSuccess: (v) => { qc.setQueryData<ChallengeView | null>(key, v); setOpen(true) },
  })
  useEffect(() => { setOpen(false) }, [personId, opportunityId])
  const view = latest.data ?? null
  const stale = view?.stale ?? false
  const count = view?.verdict.holes.length ?? 0

  return (
    <div className={`challenger ${open ? 'open' : ''}`}>
      <button type="button" className={`challenger-tab ${view ? 'has' : ''}`} onClick={() => setOpen((o) => !o)} aria-expanded={open} title="A second model reads the draft as they would">
        Challenge{view && !open ? ` · ${count}` : ''}
      </button>
      {open && (
        <aside className="challenger-panel" aria-label="The challenger">
          <header className="challenger-head">
            <h3>The challenger</h3>
            <button type="button" className="button small" onClick={() => setOpen(false)}>Close</button>
          </header>
          <p className="muted small">
            A second model reads the draft as the person it is to would, and says why they would open it, why they would
            reply, and where it is weak. It did not write the draft and owes it nothing.
          </p>
          <button type="button" className="button primary" disabled={run.isPending || busy} onClick={() => run.mutate()}>
            {run.isPending ? 'Reading it as they would…' : view ? 'Challenge it again' : 'Challenge this draft'}
          </button>
          {run.isError && <p className="error small" role="alert">{why(run.error)}</p>}
          {latest.isError && !run.isError && <p className="muted small">{why(latest.error)}</p>}
          {view && (
            <>
              {stale && <p className="small warn">The draft has changed since this was read. Challenge it again for the current text.</p>}
              <div className="challenge-verdicts">
                <Verdict question="Would they open it?" word={view.verdict.opens} reason={view.verdict.opensWhy} />
                <Verdict question="Would they reply?" word={view.verdict.replies} reason={view.verdict.repliesWhy} />
              </div>
              {view.verdict.summary && <p className="challenge-summary">{view.verdict.summary}</p>}
              {view.verdict.holes.length > 0 && (
                <ol className="plain challenge-holes">
                  {view.verdict.holes.map((h, i) => (
                    <li key={i} className="challenge-hole">
                      <div className="challenge-hole-head">
                        <span className="tag">{h.part === 'draft' ? 'the draft' : PART_WORDS[h.part] ?? h.part}</span>
                        <strong>{h.claim}</strong>
                      </div>
                      {h.why && <p>{h.why}</p>}
                      {h.instead && <p className="challenge-instead"><span className="muted">Instead:</span> {h.instead}</p>}
                      {h.instruction && (
                        <button type="button" className="button small" disabled={busy} onClick={() => onApply(h.instruction)} title="Puts this into the Wordsmith; you send it">
                          Apply in the conversation
                        </button>
                      )}
                    </li>
                  ))}
                </ol>
              )}
              {view.verdict.keep.length > 0 && (
                <p className="small challenge-keep"><span className="muted">Keep:</span> {view.verdict.keep.join(' · ')}</p>
              )}
              <p className="muted small challenge-foot">
                Challenged by {modelWords(view.modelId)}
                {view.sameModel
                  ? ' — the same model as the crafting table; name a challengerCliModel for a second pair of eyes'
                  : ` — a different model from ${modelWords(view.craftingModelId)}`}
                {view.versionSeq ? ` · on v${view.versionSeq}` : ''} · {formatRelative(view.at)}
              </p>
            </>
          )}
        </aside>
      )}
    </div>
  )
}
